import { useState } from 'react';
import { Search, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { TedSearchParams } from '../../types/opportunity.types';
import { CpvCodeMultiSelect } from './CpvCodeMultiSelect';
import {
  TED_COUNTRIES,
  TED_DOCUMENT_TYPES,
  TED_PROCEDURE_TYPES,
} from '../../constants/ted-constants';

interface TedSearchFiltersProps {
  onSearch: (params: TedSearchParams) => void;
  isLoading?: boolean;
}

export function TedSearchFilters({
  onSearch,
  isLoading,
}: TedSearchFiltersProps) {
  // Filtres principaux
  const [fullText, setFullText] = useState('');
  const [cpvCodes, setCpvCodes] = useState<string[]>([]);
  const [buyerName, setBuyerName] = useState('');
  const [country, setCountry] = useState<string>('all');
  const [contractNature, setContractNature] = useState<string[]>([]);

  // Filtres avancés
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [documentType, setDocumentType] = useState<string>('all');
  const [procedureType, setProcedureType] = useState<string>('all');
  const [publicationAfter, setPublicationAfter] = useState('');
  const [publicationBefore, setPublicationBefore] = useState('');
  const [deadlineAfter, setDeadlineAfter] = useState('');
  const [deadlineBefore, setDeadlineBefore] = useState('');

  const handleSearch = () => {
    const params: TedSearchParams = {
      fullText: fullText || undefined,
      cpvCodes: cpvCodes.length > 0 ? cpvCodes : undefined,
      buyerName: buyerName || undefined,
      countries: country && country !== 'all' ? [country] : undefined,
      contractNature:
        contractNature.length > 0
          ? (contractNature as ('services' | 'supplies' | 'works')[])
          : undefined,
      documentType: documentType !== 'all' ? documentType : undefined,
      procedureType: procedureType !== 'all' ? procedureType : undefined,
      publicationAfter: publicationAfter || undefined,
      publicationBefore: publicationBefore || undefined,
      deadlineAfter: deadlineAfter || undefined,
      deadlineBefore: deadlineBefore || undefined,
      page: 1,
      pageSize: 20,
      sortField: 'PD',
      reverseOrder: false,
    };
    onSearch(params);
  };

  const handleReset = () => {
    setFullText('');
    setCpvCodes([]);
    setBuyerName('');
    setCountry('all');
    setContractNature([]);
    setDocumentType('contract_notice');
    setProcedureType('all');
    setPublicationAfter('');
    setPublicationBefore('');
    setDeadlineAfter('');
    setDeadlineBefore('');
    onSearch({ page: 1, pageSize: 20, sortField: 'PD', reverseOrder: false });
  };

  const toggleNature = (type: string) => {
    setContractNature((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="bg-surface rounded-lg border p-6 space-y-4">
      {/* Filtres principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Recherche plein texte */}
        <div className="space-y-2 lg:col-span-2">
          <Label htmlFor="fullText">Recherche plein texte</Label>
          <Input
            id="fullText"
            placeholder="Ex: logiciel, informatique, conseil..."
            value={fullText}
            onChange={(e) => setFullText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Nom acheteur */}
        <div className="space-y-2">
          <Label htmlFor="tedBuyerName">Nom de l'acheteur</Label>
          <Input
            id="tedBuyerName"
            placeholder="Ex: Commission européenne"
            value={buyerName}
            onChange={(e) => setBuyerName(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Pays */}
        <div className="space-y-2">
          <Label htmlFor="country">Pays</Label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger>
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">Tous les pays</SelectItem>
              {TED_COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} - {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Codes CPV */}
      <div className="space-y-2">
        <Label>Codes CPV (classification)</Label>
        <CpvCodeMultiSelect
          value={cpvCodes}
          onChange={setCpvCodes}
          placeholder="Sélectionner des codes CPV..."
        />
      </div>

      {/* Type de marché */}
      <div className="space-y-2">
        <Label>Type de marché</Label>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'services', label: 'Services' },
            { value: 'supplies', label: 'Fournitures' },
            { value: 'works', label: 'Travaux' },
          ].map(({ value, label }) => (
            <Button
              key={value}
              type="button"
              variant={contractNature.includes(value) ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleNature(value)}
              className={
                contractNature.includes(value)
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : ''
              }
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {/* Filtres avancés */}
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full justify-between">
            Filtres avancés
            {showAdvanced ? (
              <ChevronUp className="w-4 h-4 ml-2" />
            ) : (
              <ChevronDown className="w-4 h-4 ml-2" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Type de document */}
            <div className="space-y-2">
              <Label htmlFor="documentType">Type de document</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {TED_DOCUMENT_TYPES.map((dt) => (
                    <SelectItem key={dt.value} value={dt.value}>
                      {dt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type de procédure */}
            <div className="space-y-2">
              <Label htmlFor="procedureType">Type de procédure</Label>
              <Select value={procedureType} onValueChange={setProcedureType}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les procédures</SelectItem>
                  {TED_PROCEDURE_TYPES.map((pt) => (
                    <SelectItem key={pt.value} value={pt.value}>
                      {pt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dates de publication */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tedPubAfter">Publiés depuis le</Label>
              <Input
                id="tedPubAfter"
                type="date"
                value={publicationAfter}
                onChange={(e) => setPublicationAfter(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tedPubBefore">Publiés jusqu'au</Label>
              <Input
                id="tedPubBefore"
                type="date"
                value={publicationBefore}
                onChange={(e) => setPublicationBefore(e.target.value)}
              />
            </div>
          </div>

          {/* Dates limites */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tedDlAfter">Date limite à partir du</Label>
              <Input
                id="tedDlAfter"
                type="date"
                value={deadlineAfter}
                onChange={(e) => setDeadlineAfter(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tedDlBefore">Date limite jusqu'au</Label>
              <Input
                id="tedDlBefore"
                type="date"
                value={deadlineBefore}
                onChange={(e) => setDeadlineBefore(e.target.value)}
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button onClick={handleSearch} disabled={isLoading} className="flex-1 bg-blue-600 hover:bg-blue-700">
          <Search className="w-4 h-4 mr-2" />
          Rechercher sur TED
        </Button>
        <Button variant="outline" onClick={handleReset} disabled={isLoading}>
          <RotateCcw className="w-4 h-4 mr-2" />
          Réinitialiser
        </Button>
      </div>
    </div>
  );
}
